import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import { Search, SlidersHorizontal } from "lucide-react";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const LANGS = ["English", "French"];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    category: params.get("category") || "",
    level: params.get("level") || "",
    language: params.get("language") || "",
    max_price: params.get("max_price") || "",
  }), [params]);

  useEffect(() => {
    api.get("/api/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) q[k] = v; });
    api.get("/api/courses", { params: q }).then(({ data }) => setCourses(data)).finally(() => setLoading(false));
  }, [filters]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-10">
        <div className="label-eyebrow">Course catalog</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-2 tracking-tight">All courses</h1>
        <p className="text-[#4B5563] mt-2 max-w-2xl">Filter by category, level, language and budget. Every course comes with a free preview.</p>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        <aside className="md:col-span-3">
          <div className="flex items-center gap-2 mb-4">
            <SlidersHorizontal className="w-4 h-4" />
            <div className="font-display font-bold">Filters</div>
          </div>

          <div className="mb-6">
            <label className="label-eyebrow block mb-2">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
              <input
                data-testid="filter-search"
                value={filters.q}
                onChange={(e) => updateFilter("q", e.target.value)}
                placeholder="Course title…"
                className="w-full border border-[#E5E7EB] rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="label-eyebrow block mb-2">Category</label>
            <select
              data-testid="filter-category"
              value={filters.category}
              onChange={(e) => updateFilter("category", e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">All categories</option>
              {cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>
          </div>

          <div className="mb-6">
            <label className="label-eyebrow block mb-2">Level</label>
            <div className="flex flex-wrap gap-2">
              {LEVELS.map((l) => (
                <button
                  key={l}
                  data-testid={`filter-level-${l}`}
                  onClick={() => updateFilter("level", filters.level === l ? "" : l)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    filters.level === l
                      ? "bg-[#D92D20] text-white border-[#D92D20]"
                      : "border-[#E5E7EB] hover:border-[#D92D20]"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="label-eyebrow block mb-2">Language</label>
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button
                  key={l}
                  data-testid={`filter-lang-${l}`}
                  onClick={() => updateFilter("language", filters.language === l ? "" : l)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    filters.language === l
                      ? "bg-[#064E3B] text-white border-[#064E3B]"
                      : "border-[#E5E7EB] hover:border-[#064E3B]"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="label-eyebrow block mb-2">Max price (USD)</label>
            <input
              data-testid="filter-max-price"
              type="number" min="0" step="5"
              value={filters.max_price}
              onChange={(e) => updateFilter("max_price", e.target.value)}
              placeholder="e.g. 50"
              className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]"
            />
          </div>

          <button data-testid="filter-clear" onClick={() => setParams({})} className="text-sm font-semibold text-[#D92D20] hover:underline">
            Clear all filters
          </button>
        </aside>

        <section className="md:col-span-9">
          <div className="text-sm text-[#4B5563] mb-4" data-testid="catalog-count">
            {loading ? "Loading..." : `${courses.length} course${courses.length !== 1 ? "s" : ""} found`}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => <CourseCard key={c.id} course={c} />)}
          </div>
          {!loading && courses.length === 0 && (
            <div className="text-center py-16 text-[#4B5563]">No courses match your filters.</div>
          )}
        </section>
      </div>
    </div>
  );
}
